import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSimpleError, useToast } from 'hooks';
import { useMutation, useConditionalSubscription } from 'features/common/security/hooks';
import SearchOfferComponent from './SearchOfferComponent';
import { RETRIEVE_EXTERNAL_OFFER_DATA, COPY_EXTERNAL_OFFER_DATA } from '../mutations';
import { EXTERNAL_OFFER_DATA_RETRIEVED, EXTERNAL_OFFER_DATA_COPIED } from '../subscriptions';
import { useChangeTrackingLens, get } from '@totalsoft/change-tracking-react';

const SearchOfferContainer = ({ documentId, insuranceTypeId, onCloseModal }) => {
  const showError = useSimpleError();
  const [searching, setSearching] = useState(false);
  const [documentNumber, setDocumentNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(false);
  const [externalOfferData, , resetExternalOfferData] = useChangeTrackingLens({ details: [] });
  const addToast = useToast();

  const [retrieveOfferDetails] = useMutation(RETRIEVE_EXTERNAL_OFFER_DATA, {
    onError: error => {
      showError(error);
      setSearching(false);
    }
  });

  const [copyDetail] = useMutation(COPY_EXTERNAL_OFFER_DATA, {
    onError: error => {
      showError(error);
      setSearching(false);
    }
  });

  const handleRetrieveOffers = documentNumber => {
    if (documentNumber !== '') {
      retrieveOfferDetails({
        variables: {
          input: {
            insuranceTypeId,
            documentNumber
          }
        }
      });
      setSearching(true);
      setLoading(true);
    }
    return;
  };

  const handleCopyItem = () => {
    if (!(externalOfferData.details |> get).some(offer => offer.selected)) {
      return;
    }

    const asset = (externalOfferData.details |> get).filter(x => x.selected);
    const data = asset[0];

    // eslint-disable-next-line no-unused-vars
    const { selected, ...partialAsset } = data;

    copyDetail({
      variables: {
        input: {
          documentId: documentId,
          asset: partialAsset,
          financingType: externalOfferData.financingType |> get,
          uniqueId: externalOfferData.uniqueId |> get,
          partnerTypeId: externalOfferData.partnerTypeId |> get,
          filterContactDataForDealer: externalOfferData.filterContactDataForDealer |> get
        }
      }
    });
  };

  useConditionalSubscription(EXTERNAL_OFFER_DATA_RETRIEVED, {
    onData: ({ data: { data } }) => {
      const result = data.externalOfferDataRetrieved;
      resetExternalOfferData(result);
      setSearching(false);
      setLoading(false);
      if (result.errorMessage)
        addToast(result.errorMessage, 'error', { toastId: 'externalOfferDataRetrievedToast' }, result.headers.nbbCorrelationId);
    }
  });

  const handleToggleSelectItem = assetId => {
    resetExternalOfferData(prev => ({
      ...prev,
      details: prev.details.map(detail =>
        detail.assetId === assetId ? { ...detail, selected: !detail.selected } : { ...detail, selected: false }
      )
    }));
    setSelected(!(externalOfferData.details |> get).some(offer => offer.selected));
  };

  const handleBackDropClick = (_, reason) => {
    if (reason && reason == 'backdropClick') {
      return;
    }
    onCloseModal();
  };

  useConditionalSubscription(EXTERNAL_OFFER_DATA_COPIED, {
    onData: onCloseModal
  });

  return (
    <SearchOfferComponent
      offerDetails={externalOfferData?.details |> get}
      loading={loading}
      searching={searching}
      documentNumber={documentNumber}
      onSetDocumentNumber={value => setDocumentNumber(value)}
      onCloseModal={handleBackDropClick}
      onToggleSelectItem={handleToggleSelectItem}
      onRetrieveOffers={handleRetrieveOffers}
      selected={selected}
      onCopyItem={handleCopyItem}
    />
  );
};

SearchOfferContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  insuranceTypeId: PropTypes.number.isRequired,
  onCloseModal: PropTypes.func.isRequired
};

export default SearchOfferContainer;
